import dayjs, { Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { WeekdaysType } from './constants'

// getTimeFromNow needs this wherever the module is loaded, not only in the app entrypoint.
dayjs.extend(relativeTime)

export type DateLike = Dayjs | Date | string

// Matches the events.time_zone database default. Used only as a runtime
// backstop when a query forgets to select timeZone — we degrade to a fixed
// zone rather than to the viewer's browser, so a data gap renders the same
// for everyone instead of differently for every reader.
export const DEFAULT_EVENT_TIME_ZONE = 'America/New_York'

// Event times are always rendered in the event's own venue zone. `timeZone` is
// REQUIRED on every formatter below: an optional zone meant a forgotten
// argument silently fell back to the reader's browser clock, which is how
// cross-zone submitters ended up seeing their own events an hour off. Making it
// required moves that mistake from "silent wrong time in production" to
// "TypeScript error at build time". If you genuinely want the reader's clock
// (a publish timestamp, a scheduled social post), call the explicitly named
// *InViewerZone helpers at the bottom of this file.
const inTz = (date: DateLike | undefined, timeZone: string): Dayjs =>
  dayjs(date as DateLike).tz(timeZone || DEFAULT_EVENT_TIME_ZONE)

// The zone any given event renders in. Every event-time render site should read
// its zone through this rather than touching `event.timeZone` directly, so the
// missing-data fallback lives in exactly one place.
export const eventTimeZone = (event?: { timeZone?: string | null } | null): string =>
  event?.timeZone || DEFAULT_EVENT_TIME_ZONE

export const dayOfTheWeek = (date: DateLike, timeZone: string): WeekdaysType =>
  inTz(date, timeZone).format('dddd') as WeekdaysType

export const formatTime = (date: DateLike, timeZone: string): string => inTz(date, timeZone).format('h:mma')
export const formatDate = (date: Date | string, timeZone: string): string => inTz(date, timeZone).format('MMMM D, YYYY')
export const formatDateAndTime = (date: DateLike, timeZone: string): string =>
  inTz(date, timeZone).format('ddd, MMM Do @ h:mma')
export const formatDayAndDate = (date: Date | string, timeZone: string): string =>
  inTz(date, timeZone).format('ddd, MMM D, YYYY')

export const formatMonthDay = (date: DateLike, timeZone: string): string => inTz(date, timeZone).format('MMMM Do')

export const formatShortDate = (date: DateLike, timeZone: string): string => inTz(date, timeZone).format('MM/DD/Y')

export const formatLongDate = (date: DateLike, timeZone: string): string => inTz(date, timeZone).format('MMM DD, Y')

export const isSameDay = (start: DateLike | undefined, end: DateLike | undefined, timeZone: string): boolean =>
  inTz(start, timeZone).isSame(inTz(end, timeZone), 'day')

// Short label shown next to a rendered event time, e.g. "ET". Daylight and
// standard variants are collapsed (EDT/EST both read "ET") so the label stays
// stable across the year and matches how people actually say it. Zones outside
// the four US continental ones keep whatever Intl reports.
export const timeZoneAbbreviation = (timeZone: string, date: DateLike = new Date()): string => {
  const zone = timeZone || DEFAULT_EVENT_TIME_ZONE
  let raw = ''

  try {
    raw =
      new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(dayjs(date).toDate())
        .find(part => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }

  return /^[ECMP][DS]T$/.test(raw) ? `${raw[0]}T` : raw
}

export const getTimeFromNow = (date?: DateLike): string => dayjs(date).fromNow()

export const getDuration = (start: DateLike, end: DateLike = new Date()): string =>
  dayjs.duration(dayjs(start).diff(dayjs(end))).humanize()

// Reader's-clock formatters, for two narrow cases only:
//   1. timestamps describing an action taken in the app (published at,
//      scheduled for, posted at) rather than when an event happens; and
//   2. values still being edited in a form, where the Date object encodes the
//      naive wall-clock the user typed and re-zoning it would shift what they
//      just entered out from under them.
// Never use these to display a saved event's start or end time.
export const formatDateInViewerZone = (date: Date | string): string => dayjs(date).format('MMMM D, YYYY')

export const formatShortDateInViewerZone = (date: DateLike): string => dayjs(date).format('MM/DD/Y')

export const formatTimeInViewerZone = (date: DateLike): string => dayjs(date).format('h:mma')

export const formatDayAndDateInViewerZone = (date: Date | string): string => dayjs(date).format('ddd, MMM D, YYYY')

export const dayOfTheWeekInViewerZone = (date: DateLike): WeekdaysType => dayjs(date).format('dddd') as WeekdaysType
